import React, { useState, useEffect } from 'react';
import { Menu, X, Phone } from 'lucide-react';
import { businessInfo } from '../data/mock';

const navLinks = [
  { name: 'Treatments', href: '#services' },
  { name: 'Peptides', href: '#peptides' },
  { name: 'Studio', href: '#about' },
  { name: 'Results', href: '#results' },
  { name: 'Contact', href: '#contact' },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-onyx/85 backdrop-blur-xl border-b border-pewter/60'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Wordmark */}
          <a href="#home" className="flex items-center gap-3 group" data-testid="brand-wordmark">
            <img
              src="/logo.png"
              alt="Snatched Beauties Privé"
              className="h-11 w-11 md:h-12 md:w-12 object-contain"
            />
            <div className="hidden sm:flex flex-col leading-none">
              <span className="font-serif text-ivory text-[17px] tracking-wide">
                Snatched Beauties <span className="brand-italic chrome-rose">Privé</span>
              </span>
              <span className="text-[10px] tracking-[0.32em] uppercase text-silverx mt-1">
                by Snatched Beauties LLC
              </span>
            </div>
          </a>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-10">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className="text-[13px] tracking-[0.18em] uppercase text-platinum hover:text-blush transition-colors duration-300"
              >
                {link.name}
              </a>
            ))}
          </div>

          {/* Right Actions */}
          <div className="hidden lg:flex items-center gap-6">
            <a
              href={`tel:${businessInfo.phone}`}
              className="text-[12px] tracking-[0.2em] uppercase text-silverx hover:text-ivory transition-colors flex items-center gap-2"
              data-testid="header-phone"
            >
              <Phone size={13} /> {businessInfo.phone}
            </a>
            <a
              href={businessInfo.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-book-btn"
              className="btn-pill inline-flex items-center px-6 py-[14px] rounded-full bg-ivory text-onyx text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-blush"
            >
              Book Consultation
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-ivory"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            className="lg:hidden bg-carbon/95 backdrop-blur-xl rounded-none border-t border-pewter/50 -mx-5 sm:-mx-8 px-6 py-9"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-5">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-platinum hover:text-blush py-1 text-[13px] tracking-[0.22em] uppercase"
                >
                  {link.name}
                </a>
              ))}
              <div className="hairline mt-5 mb-3" />
              <a
                href={`tel:${businessInfo.phone}`}
                className="text-silverx text-[12px] tracking-[0.2em] uppercase flex items-center gap-2 py-2"
              >
                <Phone size={13} /> {businessInfo.phone}
              </a>
              <a
                href={businessInfo.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsMobileMenuOpen(false)}
                className="btn-pill mt-2 inline-flex justify-center items-center px-6 py-3 rounded-full bg-ivory text-onyx text-[11px] tracking-[0.28em] uppercase font-medium"
                data-testid="mobile-book-btn"
              >
                Book Consultation
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
